import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from 'recharts';
import { SpotifyLogo, YoutubeLogo, TrendUp, Waveform } from '@phosphor-icons/react';
import { useAuth } from '@/lib/auth';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get('/analytics/overview').then(r => setData(r.data));
  }, []);

  if (!data) return <div className="p-8 font-mono text-zinc-500">Loading analytics…</div>;

  const fmt = (v) => (v === null || v === undefined) ? '—' : v.toLocaleString();
  const anyLive = data.spotify.live || data.youtube.live;

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <header className="mb-8 flex items-end justify-between">
        <div>
          <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Dashboard</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold mt-1">Hi, {user?.name?.split(' ')[0] || 'artist'}.</h1>
          <p className="text-zinc-400 mt-1 text-sm">Everything from Spotify & YouTube, plus what's running on ads and curators.</p>
        </div>
        <div data-testid="dashboard-live-badge" className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full border border-zinc-800 bg-zinc-900/60 text-xs font-mono text-zinc-400">
          <span className={`w-1.5 h-1.5 rounded-full ${anyLive ? 'bg-[#ccff00]' : 'bg-zinc-500'}`} /> {anyLive ? 'live data' : 'demo data · connect accounts for real numbers'}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {data.spotify.live ? (
          <MetricCard testid="metric-spotify-listeners" icon={<SpotifyLogo size={18} weight="fill" className="text-[#1DB954]" />}
            label="Spotify followers" value={fmt(data.spotify.followers)} delta="live" />
        ) : (
          <MetricCard testid="metric-spotify-listeners" icon={<SpotifyLogo size={18} weight="fill" className="text-[#1DB954]" />}
            label="Monthly listeners (Spotify)" value={fmt(data.spotify.monthly_listeners)} delta="+12.4%" />
        )}
        <MetricCard testid="metric-yt-subs" icon={<YoutubeLogo size={18} weight="fill" className="text-red-500" />}
          label="YouTube subscribers" value={fmt(data.youtube.subscribers)} delta={data.youtube.live ? 'live' : '+8.1%'} />
        {data.spotify.live ? (
          <MetricCard testid="metric-streams" icon={<TrendUp size={18} weight="duotone" className="text-[#ccff00]" />}
            label="Top track popularity" value={data.top_songs[0] ? `${Math.round(data.top_songs[0].streams / 210)}/100` : '—'} delta="live" />
        ) : (
          <MetricCard testid="metric-streams" icon={<TrendUp size={18} weight="duotone" className="text-[#ccff00]" />}
            label="Total streams (30d)" value={fmt(data.spotify.streams_30d)} delta="+21.7%" />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 border border-zinc-800 rounded-xl p-6 bg-zinc-900/40">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">{data.spotify.live ? 'Follower growth · last 14 days' : 'Streams last 14 days'}</div>
              <h3 className="font-display text-xl mt-1">Momentum</h3>
            </div>
            <Waveform size={22} weight="duotone" className="text-[#ccff00]" />
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={data.spotify.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="day" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} />
                <Tooltip contentStyle={{ background: '#09090b', border: '1px solid #27272a', borderRadius: 8 }} />
                <Line type="monotone" dataKey="value" stroke="#ccff00" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-zinc-800 rounded-xl p-6 bg-zinc-900/40">
          <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">{data.spotify.live ? 'Top tracks' : 'Top songs'}</div>
          <h3 className="font-display text-xl mt-1 mb-4">Right now</h3>
          <div className="space-y-3">
            {data.top_songs.map((s, i) => (
              <div key={s.title} className="flex items-center justify-between border-b border-zinc-800/60 pb-3 last:border-0">
                <div className="flex items-center gap-3">
                  <div className="font-display text-sm text-zinc-500 w-4">{i + 1}</div>
                  <div>
                    <div className="text-sm font-medium">{s.title}</div>
                    <div className="text-xs font-mono text-zinc-500">{data.spotify.live ? `popularity ${Math.round(s.streams / 210)}/100` : `${s.streams.toLocaleString()} streams`}</div>
                  </div>
                </div>
                {!data.spotify.live && <div className="text-xs font-mono text-[#ccff00]">+{(Math.random() * 30 + 5).toFixed(0)}%</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {data.spotify.live ? (
          <MetricCard testid="metric-saves" label="Followers" value={fmt(data.spotify.followers)} delta="live" small />
        ) : (
          <MetricCard testid="metric-saves" label="Saves (30d)" value={fmt(data.spotify.saves_30d)} delta="+9%" small />
        )}
        <MetricCard testid="metric-followers" label="Spotify followers" value={fmt(data.spotify.followers)} delta={data.spotify.live ? 'live' : '+4%'} small />
        <MetricCard testid="metric-views" label="YouTube views (30d)" value={fmt(data.youtube.views_30d)} delta={data.youtube.live ? 'live' : '+15%'} small />
        <MetricCard testid="metric-watch" label="Watch hours" value={fmt(data.youtube.watch_hours)} delta={data.youtube.live ? 'live' : '+6%'} small />
      </div>
      {(data.spotify.live || data.youtube.live) && (
        <p className="mt-4 text-xs font-mono text-zinc-600">
          Note: Spotify's public API doesn't expose monthly-listener/stream counts (Spotify-for-Artists only) — followers and top-track popularity above are real, pulled live from your connected account.
        </p>
      )}
    </div>
  );
}

function MetricCard({ icon, label, value, delta, small, testid }) {
  return (
    <div data-testid={testid} className="border border-zinc-800 rounded-xl bg-zinc-900/40 card-hover p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
        {icon}
      </div>
      <div className={`font-display font-bold ${small ? 'text-2xl' : 'text-3xl'} mt-1`}>{value}</div>
      <div className="mt-2 text-xs font-mono text-[#ccff00]">{delta}</div>
    </div>
  );
}
